import type * as React from "react";

import { PaperPanel, type PaperPanelProps } from "./PaperPanel";
import { cn } from "../lib/cn";

/** Which paper object is carrying the card. */
export type PressableCardHost = "panel" | "note";

/*
 * The lift, shared by every host. Paper hover is a lift, never a recolor, and
 * `translate` is its own CSS property in Tailwind v4 rather than part of
 * `transform`, which is what lets it compose with a caller's own inline
 * `transform` instead of overwriting it. Keep it a translate utility.
 */
const LIFT = [
  "transition duration-150 ease-paper motion-reduce:transition-none",
  "hover:-translate-y-px hover:shadow-lift-lg",
].join(" ");

/*
 * The face, shared by every host.
 *
 * `flex-col` is not decoration: a <button> centers its content box vertically
 * whatever its display, so a stretched face without it floats its writing in
 * the middle of the card and any `mt-auto` footer has nothing to push against.
 *
 * A button also resets font-family and color to the UA's, so both are restated.
 * The focus ring is inset (`-outline-offset-2`): the face fills the paper edge
 * to edge, so an outset ring would be drawn outside the card it belongs to.
 */
const FACE = [
  "flex cursor-pointer flex-col text-left font-body text-ink",
  "focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-accent",
].join(" ");

/**
 * The two halves of "the whole card is one click target", per paper host: the
 * classes the surface wears, and the classes its inner face wears. They are
 * published as a pair because they are one mechanism split across two elements
 * - the padding leaves one and lands on the other - and getting only half of it
 * right yields a card with a dead margin no click reaches.
 *
 * `panel` moves the padding: a `PaperPanel` has no padding of its own, so the
 * surface drops to `p-0` and the face restates it, at `w-full`.
 *
 * `note` cancels and restates: a `StickyNote` bakes its own `p-4` and puts its
 * children inside a wrapper, so the face pulls itself back out with `-m-4`,
 * writes the padding again, and mirrors the note's `min-h-40` so it reaches the
 * bottom edge of a note that has not been filled. It takes no `w-full`, and
 * that is the one thing here that cannot be copied across: a percentage width
 * is resolved against the containing block, so a negative margin cannot widen
 * a `w-full` box - it only slides it left and leaves the padding uncovered on
 * the right. The face has to keep `width: auto` and let the margins do the
 * widening. `flex` already makes the button block-level, so auto fills.
 *
 * Reach for this directly only when hosting the face on a surface `RecordCard`
 * does not cover. `IndexCard` is a third shape and is deliberately absent: its
 * padded box is the ruled writing area rather than its root, and a face laid on
 * it also has to keep `leading-[var(--ruling)]` so the writing stays on the
 * lines (see the app's PatientCard).
 */
export const pressableCardChrome: Record<
  PressableCardHost,
  { readonly surface: string; readonly face: string }
> = {
  panel: { surface: `p-0 ${LIFT}`, face: `${FACE} w-full p-3` },
  note: { surface: LIFT, face: `${FACE} -m-4 min-h-40 p-4` },
};

export interface PressableFaceProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "aria-label" | "children"
> {
  /** The paper this face is laid on. Decides how it reclaims the host's padding. */
  host: PressableCardHost;
  /** What clicking the card does. A face with nothing to do is not a face. */
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  /**
   * The accessible name. Without one the name is the writing on the card, which
   * says what the card is but not what opening it does - so give the verb
   * ("Open view Discharge summary"), especially where the writing is clamped.
   */
  label?: string;
  /**
   * Phrasing content only. This is the inside of a `<button>`, so a `<p>`,
   * `<div>` or heading here is invalid HTML: write the card with `<span>` and
   * `<time>`, which are phrasing and survive the move.
   */
  children: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * A card's full-bleed click target: one `<button>` covering the whole sheet,
 * blank paper included, so there is no dead margin and no small target to aim
 * for. Pair it with the matching `pressableCardChrome[host].surface` on the
 * paper itself - the padding it reclaims is the padding the surface gave up.
 *
 * `RecordCard` already does both halves for a `PaperPanel`. Use this directly
 * for the other paper objects, which bring layout of their own that a wrapper
 * component would have to re-declare (a `StickyNote`'s adhesive band, an
 * `IndexCard`'s ruling).
 *
 * `type` is not a prop: a card is never a submit button.
 */
export function PressableFace({
  host,
  onClick,
  label,
  children,
  className,
  ...rest
}: PressableFaceProps) {
  return (
    <button
      {...rest}
      type="button"
      onClick={onClick}
      aria-label={label}
      className={cn(pressableCardChrome[host].face, className)}
    >
      {children}
    </button>
  );
}

export interface RecordCardProps extends Omit<
  PaperPanelProps,
  "onClick" | "children"
> {
  /**
   * Give one and the whole sheet becomes a single button; leave it off and the
   * card is inert writing. The swap is the point: the same card appears in
   * lists that open a record and in lists that only display one.
   */
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  /** The pressable card's accessible name. Ignored without `onClick`. */
  label?: string;
  /**
   * Phrasing content only, whether or not this card is pressable: the writing
   * has to be valid inside the `<button>` the card grows when `onClick` arrives,
   * and a card that is only legal in one of its two forms is a trap.
   */
  children: React.ReactNode;
}

/**
 * One record as a sheet of paper: a row in a drawer of views, transcripts,
 * artifacts, facts, tasks. `p-3` of writing on a `PaperPanel`, and where the
 * caller offers one, the entire sheet is the target that opens the record.
 *
 * Compose the writing from `CardHeaderRow` (title plus a trailing badge) and
 * `FilingLine` (the mono footer). Both are phrasing-safe by default, which is
 * what keeps a card legal once it becomes a button.
 *
 * Padding is `p-3` and it moves: on a pressable card it lives on the button, so
 * a `className` of `p-4` reaches only the inert form. A pressable card that
 * needs different padding wants `pressableCardChrome` and `PressableFace`
 * directly.
 *
 * For a memo on a `StickyNote` or a task on an `IndexCard`, keep that object and
 * use `PressableFace` with its `pressableCardChrome` surface - the lift is the
 * same, the padding mechanics are not.
 */
export function RecordCard({
  onClick,
  label,
  children,
  className,
  ...rest
}: RecordCardProps) {
  return (
    <PaperPanel
      className={cn(
        onClick ? pressableCardChrome.panel.surface : "p-3",
        className,
      )}
      {...rest}
    >
      {onClick ? (
        <PressableFace host="panel" onClick={onClick} label={label}>
          {children}
        </PressableFace>
      ) : (
        children
      )}
    </PaperPanel>
  );
}
